/**
 * Fundraising - port from `1116/omega-club/dashboard/src/components/Fundraising.jsx`.
 * Reads from `useTenantData(slug).campaignPhases / .giftChart / .donorCapacityTiers`.
 *
 * Per CHAP-83 (Phase 1C #49).
 *
 * Parity notes vs omega-club:
 * - **LXA Foundation Campaign Contract** block (omega-club's CHAP-262
 *   hardcoded $7,500/mo × 18 = $135K contract section) - omitted.  legacy-ok CHAP-423:
 *   a parity note recording which section was NOT ported. Those terms are
 *   retired and blacklisted; the operative agreement is $153,000
 *   ($17,000 signing + $8,500/mo), and is unsettled again since 2026-08-05.
 *   Lives in `tenant_contracts` schema in the v2 plan; not yet in
 *   TenantConfig. Separate ticket.
 * - **LCAEF Engagement Tracker** (CHAP-248) - omitted; depends on the
 *   same `tenant_contracts` + `partner_engagement_milestones` shape.
 * - **Donor Segments**, **Engagement Opportunity stats**, **Referral
 *   count**, **Survey Highlights donuts**, **Case for Support 7 Q&A**
 *   - all hardcoded data in omega-club. Schema fields for these would
 *   be tenant_survey_highlights / tenant_referrals / tenant_case_for_support.
 *   Omitted from v1 port.
 * - **Gift Pipeline Tracker** (giftTracking) - omitted; chapter-build
 *   currently surfaces gifts via campaignPhases + giftChart only.
 *
 * What ships:
 * - Header with overall % progress callout
 * - Per-phase campaign progress bars
 * - Gift Chart bar chart (needed vs known prospects)
 * - Donor Capacity pie chart by wealth tier
 * - Gift calculator (CHAP-301): per-vehicle after-tax cost of a gift,
 *   educational-space ruling aware, cabinet+ save-scenario
 * - Lagging conversations (CHAP-302): stalled donor conversations with
 *   one-click follow-up logging (cabinet+ only)
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { GiftCalculator } from '../components/fundraising/GiftCalculator.js';
import { LaggingConversations } from '../components/fundraising/LaggingConversations.js';
import { formatCurrency, formatCurrencyFull } from '../lib/formatters.js';

export default function Fundraising() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const campaignTotals = useMemo(() => {
    if (!data) return { target: 0, current: 0 };
    return data.campaignPhases.reduce(
      (acc, p) => ({
        target: acc.target + (p.targetAmount ?? 0),
        current: acc.current + p.currentAmount,
      }),
      { target: 0, current: 0 },
    );
  }, [data]);

  const totalCapacity = useMemo(() => {
    if (!data) return 0;
    return data.donorCapacityTiers.reduce((s, t) => s + (t.totalCapacity ?? 0), 0);
  }, [data]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;

  const overallProgressPct = campaignTotals.target > 0
    ? (campaignTotals.current / campaignTotals.target) * 100
    : 0;

  // Map donorCapacityTiers to a chart-friendly shape (label derived from range)
  const donorPieData = data.donorCapacityTiers.map((t) => ({
    tier: tierLabel(t.rangeLow, t.rangeHigh),
    value: t.totalCapacity ?? 0,
    color: t.color ?? '#94a3b8',
  }));

  // Map giftChart rows to a label-friendly shape for the bar chart
  const giftChartData = data.giftChart.map((g) => ({
    tier: g.giftSize ? formatCurrency(g.giftSize) : 'n/a',
    needed: g.giftsNeeded ?? 0,
    known: g.knownProspects ?? 0,
  }));

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Header with hero callout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Fundraising</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">Campaign progress, donor capacity, and gift chart</p>
          </div>
          {campaignTotals.target > 0 && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">Overall progress</span>
              <span className="text-orange text-2xl sm:text-3xl font-bold">{overallProgressPct.toFixed(0)}%</span>
              <span className="text-xs text-neutral">
                {formatCurrency(campaignTotals.current)} of {formatCurrency(campaignTotals.target)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Campaign Progress per-phase */}
      {data.campaignPhases.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy font-semibold text-sm sm:text-base mb-3">Campaign Progress by Phase</h3>
          <div className="space-y-3">
            {data.campaignPhases
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((phase) => {
                const pct = phase.targetAmount && phase.targetAmount > 0
                  ? (phase.currentAmount / phase.targetAmount) * 100
                  : 0;
                const isActive = phase.status === 'active';
                return (
                  <div key={phase.id} className="min-w-0">
                    <div className="flex items-baseline justify-between mb-1.5 flex-wrap gap-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-navy text-sm font-semibold truncate">{phase.name}</span>
                        {isActive && (
                          <span className="text-[10px] uppercase tracking-wider bg-orange/10 text-orange px-1.5 py-0.5 rounded font-semibold flex-shrink-0">
                            Active
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-neutral whitespace-nowrap flex-shrink-0">
                        {formatCurrency(phase.currentAmount)} / {formatCurrency(phase.targetAmount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isActive ? 'bg-orange' : 'bg-graph-blue'}`}
                        style={{ width: `${Math.max(pct, 1.5)}%` }}
                      />
                    </div>
                    {(phase.periodStart || phase.periodEnd) && (
                      <p className="text-neutral text-[10px] mt-1">
                        {phase.periodStart ?? 'n/a'} → {phase.periodEnd ?? 'n/a'} · {pct.toFixed(0)}%
                      </p>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Gift Chart + Donor Capacity (side by side on lg) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Gift Chart bar */}
        {giftChartData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Gift Chart - Needed vs Known Prospects</h3>
            <div className="h-48 sm:h-60 lg:h-72 overflow-x-auto">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={giftChartData} layout="vertical" margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="tier" tick={{ fontSize: 12, fill: '#64748b' }} width={60} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="needed" name="Needed" fill="#3B82F6" radius={[0, 2, 2, 0]} />
                  <Bar dataKey="known"  name="Known Prospects" fill="#22C55E" radius={[0, 2, 2, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Donor Capacity pie */}
        {donorPieData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
            <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Donor Capacity by Wealth Tier</h3>
            <div className="h-48 sm:h-60 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donorPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    nameKey="tier"
                    label={(props: PieLabelRenderProps) => {
                      // recharts 3: nameKey value arrives via payload (data object is
                      // spread into the sector item) / props.name; percent is optional.
                      const tier =
                        (props.payload as { tier?: string } | undefined)?.tier ??
                        String(props.name ?? '');
                      const percent = props.percent ?? 0;
                      return `${tier} (${(percent * 100).toFixed(0)}%)`;
                    }}
                    labelLine={{ stroke: '#64748b' }}
                  >
                    {donorPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrencyFull(value as number)}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <p className="text-center text-neutral text-xs sm:text-sm mt-2">
              Total Estimated Capacity: <span className="text-navy font-semibold">{formatCurrency(totalCapacity)}</span>
            </p>
          </div>
        )}
      </div>

      {/* Lagging conversations (CHAP-302) - renders only for cabinet+ */}
      <LaggingConversations slug={slug ?? ''} />

      {/* Gift calculator (CHAP-301) */}
      <GiftCalculator slug={slug ?? ''} />

      {/* Read-only / scope signal */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 text-xs sm:text-sm text-amber-800">
        <p className="font-semibold">Phase 1C #49 partial port.</p>
        <p className="text-amber-700 mt-1">
          The omega-club Fundraising surface also has LCAEF Engagement Tracker (CHAP-248), donor segments, survey
          highlights, referral count, Case for Support Q&A, and the LXA Foundation contract block. Those depend on
          schema fields not yet in TenantConfig (tenant_contracts, partner_engagement_milestones, survey_highlights,
          case_for_support, etc.) and ship as a follow-up.
        </p>
      </div>
    </div>
  );
}

function tierLabel(low: number | null, high: number | null): string {
  if (low == null && high == null) return 'Unknown';
  if (low != null && high == null) return `$${formatTierAmount(low)}+`;
  if (low == null && high != null) return `< $${formatTierAmount(high)}`;
  return `$${formatTierAmount(low!)} – $${formatTierAmount(high!)}`;
}

function formatTierAmount(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toString();
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
